import React from 'react';
import { Tabs } from '@mantine/core';
import type { AdminMergeAuditView, AdminMergeOrderlineSnapshot, AdminMergeTableSnapshot } from 'shared-models/browser';
import { formatDateTime } from '../../monitoring-page.utils';
import classes from './merge-audit-detail.module.scss';

type Props = {
  row: AdminMergeAuditView;
};

const kindLabels: Record<string, string> = {
  merge: 'מיזוג',
  partial_split: 'פיצול חלקי',
  full_split: 'פיצול מלא',
};

const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className={classes.detailField}>
    <span className={classes.detailLabel}>{label}</span>
    <span className={classes.detailValue}>{value ?? '—'}</span>
  </div>
);

const tableNames = (tables: AdminMergeTableSnapshot[]) =>
  tables.length > 0 ? tables.map((t) => t.tableName).join(', ') : '—';

const OrderlinesTable: React.FC<{ orderlines: AdminMergeOrderlineSnapshot[] }> = ({ orderlines }) => {
  if (orderlines.length === 0) {
    return <div className={classes.emptyTab}>{'אין פריטים בהזמנה.'}</div>;
  }
  return (
    <table className={classes.itemsTable}>
      <thead>
        <tr>
          <th>{'פריט'}</th>
          <th>{'כמות'}</th>
          <th>{'מחיר'}</th>
        </tr>
      </thead>
      <tbody>
        {orderlines.map((line) => (
          <tr key={line.orderlineId}>
            <td>{line.productName ?? 'פריט כללי'}</td>
            <td>{`×${line.quantity}`}</td>
            <td>{`₪${Number(line.unitPrice).toFixed(2)}`}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const MergeAuditDetail: React.FC<Props> = ({ row }) => {
  const kindLabel = kindLabels[row.kind] ?? row.kind;
  const isSplit = row.kind === 'partial_split' || row.kind === 'full_split';

  const splitOffTables = isSplit
    ? row.mergedTables.filter((t) => !row.mergedTablesAfter.some((a) => a.tablePk === t.tablePk))
    : [];

  return (
    <div className={classes.root}>

      <div className={classes.detailsGrid}>
        <div className={classes.detailsSection}>
          <h3 className={classes.sectionTitle}>{'פרטי פעולה'}</h3>
          <DetailField label="תאריך" value={formatDateTime(row.createdAt)} />
          <DetailField label="סוג" value={kindLabel} />
          <DetailField label="מלצר" value={row.performedByName} />
          <DetailField label="סשן" value={row.sessionName} />
          <DetailField label="הזמנה" value={row.orderCode} />
        </div>

        <div className={classes.detailsSection}>
          <h3 className={classes.sectionTitle}>{'שולחנות'}</h3>
          {isSplit ? (
            <>
              <DetailField label="שולחן ראשי" value={row.mainTableName} />
              <DetailField label="קבוצה לפני פיצול" value={tableNames(row.mergedTables)} />
              <DetailField label="שולחנות שפוצלו" value={tableNames(splitOffTables)} />
              {row.kind === 'partial_split' && (
                <DetailField label="קבוצה אחרי פיצול" value={tableNames(row.mergedTablesAfter)} />
              )}
            </>
          ) : (
            <>
              <DetailField label="שולחן ראשי" value={row.mainTableName} />
              <DetailField label="קבוצת שולחנות" value={tableNames(row.mergedTables)} />
            </>
          )}
        </div>
      </div>

      <Tabs defaultValue="orderlines" className={classes.tabs}>
        <Tabs.List>
          <Tabs.Tab value="orderlines" data-testid="monitoring-merge-detail-tab-orderlines">{`פריטים בהזמנה (${row.orderlines.length})`}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="orderlines" className={classes.tabPanel}>
          <OrderlinesTable orderlines={row.orderlines} />
        </Tabs.Panel>
      </Tabs>

    </div>
  );
};
